import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

const Register = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    username: '',
    password: '',
    confirm_password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(true);

  const validateInvite = useCallback(async () => {
    if (!token) {
      setError('Ungültiger Einladungslink');
      setInviteLoading(false);
      return;
    }

    try {
      const data = await apiService.validateInvite(token);
      if (data.valid) {
        setInviteValid(true);
      } else {
        setError(data.error || 'Einladungslink ist ungültig oder abgelaufen');
      }
    } catch (error) {
      setError('Fehler beim Überprüfen des Einladungslinks');
    } finally {
      setInviteLoading(false);
    }
  }, [token]);

  useEffect(() => {
    validateInvite();
  }, [validateInvite]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validierung
    if (formData.password !== formData.confirm_password) {
      setError('Die Passwörter stimmen nicht überein');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      setIsLoading(false);
      return;
    }

    if (!formData.first_name.trim() || !formData.username.trim()) {
      setError('Bitte fülle alle Felder aus');
      setIsLoading(false);
      return;
    }

    try {
      const data = await apiService.register({
        first_name: formData.first_name,
        username: formData.username,
        password: formData.password,
        invite_token: token
      });

      if (data.success) {
        // Erfolgreiche Registrierung
        alert('Registrierung erfolgreich! Du kannst dich jetzt anmelden.');
        navigate('/');
      } else {
        setError(data.error || 'Fehler bei der Registrierung');
      }
    } catch (error) {
      setError('Fehler bei der Registrierung. Bitte versuche es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen bg-bg-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-1 mx-auto mb-4"></div>
            <p className="text-text-normal">Überprüfe Einladungslink...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen bg-bg-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl">
          <div className="text-center">
            <div className="text-4xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-text-strong mb-4">Ungültiger Einladungslink</h1>
            <p className="text-text-subtle mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary w-full"
            >
              Zurück zur Anmeldung
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl">
        <div className="text-center mb-8">
          <div className="text-4xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-text-strong mb-2">Willkommen beim Wachtel Stammtisch!</h1>
          <p className="text-text-subtle">Erstelle dein Konto mit dem Einladungslink</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-strong mb-1">
              Vorname *
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-accent-2 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent transition-colors"
              placeholder="Dein Vorname"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-strong mb-1">
              Benutzername *
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-accent-2 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent transition-colors"
              placeholder="Dein Benutzername"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-strong mb-1">
              Passwort *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength={6}
              className="w-full px-4 py-3 border border-accent-2 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent transition-colors"
              placeholder="Mindestens 6 Zeichen"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-strong mb-1">
              Passwort bestätigen *
            </label>
            <input
              type="password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-accent-2 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent transition-colors"
              placeholder="Passwort wiederholen"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-accent-1 hover:bg-accent-2 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Registriere...' : 'Konto erstellen'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-text-subtle">
            Bereits ein Konto?{' '}
            <button
              onClick={() => navigate('/')}
              className="text-accent-1 hover:text-accent-2 font-medium"
            >
              Hier anmelden
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
